<template>
    <form>
        <div class="flex gap-1 p-[0.3571429rem] border border-gray-300 rounded-[0.4285714rem] mb-3">
            <label :for="id" class="sr-only">Email Address</label>
            <BaseInput :id="id" aria-label="Enter your email address" placeholder="Email Address" type="email" class="pt-[0.3571429rem] px-[1rem] pb-[0.4285714rem] focus:outline-none focus:ring-0 w-full"
                v-model="email" :bind="emailAttrs" :disabled="isSubmitting" />

            <button :id="`btn-${id}`" aria-label="Submit email form"
                class="bg-gray-900 text-white rounded-md px-[1rem] pt-[0.5rem] pb-[0.4285714rem] border border-gray-900 transition-colors ease-in duration-100 focus:bg-gray-700 hover:bg-gray-700"
                @click="submitEmailSignup" :disabled="isSubmitting">

                <Icon icon='paper-plane'/>
            </button>
        </div>
        <div class="rounded-md bg-gray-300 text-red-700 text-center p-2 flex items-center gap-2" aria-live="polite" role="alert" v-if="errors.email">
          <span class="sr-only">Error</span>
          <span class="icon error-icon">
            <Icon icon='triangle-exclamation' packPrefix="fas"/>
          </span>
          <p class="m-auto text-black">{{ errors.email }}</p>
        </div>
    </form>
</template>

<script setup lang="ts">

import {useForm} from 'vee-validate'
import {z} from 'zod'
import {toTypedSchema} from '@vee-validate/zod'
import debounce from 'debounce'
import Icon from '../atom/icon.vue'
import BaseInput from '../atom/baseInput.vue'
import {useSecurityStore} from '~/stores/securityStore'
import { type EventFactory } from '@thorne/hightouch-clickstream'

type ContextType = 'footer' | 'take5DailySidebar'

interface EmailSubscriptionFormProps {
    listNames?: string | string[],
    messageNames?: string[],
    marketingChannel?: boolean,
    context: ContextType,
    id?: string
}

const emit = defineEmits(['onSuccess', 'onError']);
const props = defineProps<EmailSubscriptionFormProps>();
const nuxtApp = useNuxtApp();

// validation
const invalidEmailMessage = "Invalid Email Address";
const emptyErrorMessage = "Email address is required";
const validationSchema = toTypedSchema(
    z.object({  
        email: z.string().min(1, { message: emptyErrorMessage }).email(invalidEmailMessage)
    })
);

// define fields using our validation schema
const {errors, isSubmitting, handleSubmit, defineField} = useForm({validationSchema, initialValues: {email: '' }});
const [email, emailAttrs] = defineField('email');

const securityStore = useSecurityStore();
const submitEmailSignup = handleSubmit(
    debounce(async (values) => {
      if (!securityStore.data?.csrf) {
        await securityStore.fetch();
      }

      const csrf = securityStore.data?.csrf;
      if (!csrf) {
        emit('onError', new Error('CSRF token missing'));
        return;
      }

      const isFooter = props.context === 'footer';
      const apiEndpoint = isFooter ? '/email/multi/subscribe' : '/email/list/subscribe';
      const reqBody = {
        email: values.email,
        [isFooter ? 'lists' : 'listName']: props.listNames,
        ...(isFooter && { marketingChannel: props.marketingChannel }),
        ...(isFooter && { messages: props.messageNames })
      }
      $fetch(apiEndpoint, {
        method: 'POST',
        headers: {
          [csrf.headerName]: csrf.token
        },
        body: reqBody
      })
      .then((res) => {
        emit('onSuccess', res)
        globalThis.setTimeout(() => {
          sendClickstreamIdentifyEvent(values.email)
        }, 0)
      })
      .catch(err => emit('onError', err));
    }, 500, { immediate: true }));

/**
 * Sends a Hightouch identify event for a successful email subscription.
 *
 * Email subscription events intentionally use an anonymous user id and blank
 * name while passing the submitted email address as an identify trait. The
 * Hightouch plugin can be disabled or unavailable in some environments, and
 * analytics failures must never interrupt the form success flow.
 */
const sendClickstreamIdentifyEvent = (email: string) => {
  const hightouchEvents = nuxtApp.$hightouchEvents as EventFactory | null | undefined;

  if (!email || !hightouchEvents) return;

  try {
    hightouchEvents.identify('', {
      email,
      name: ''
    }, {
      callback: (error) => {
        if (error) {
          console.warn('Failed to send Hightouch identify event - email subscription', error)
        }
      }
    })
  } catch (error) {
    if (error) {
      console.warn('Error when sending Hightouch identify event - email subscription', error)
    }
  }
};

</script>
 
